import { useState, useEffect, useRef } from 'react';
import { getUrlWithToken } from '../utils/urlUtils';
import { translationApi } from '../services/translationApi';

export interface ProgressState {
    status: 'pending' | 'translating' | 'completed' | 'failed' | 'idle';
    stage: string;
    error?: string;
    downloadUrl?: string;
}

export function useTranslationProgress(taskId: string | null) {
    const [progress, setProgress] = useState<ProgressState>({
        status: 'idle',
        stage: '',
    });
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (!taskId) {
            setProgress({ status: 'idle', stage: '' });
            return;
        }

        // 清理之前的轮询
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }

        setProgress({ status: 'pending', stage: '连接服务器...' });

        let sseTerminated = false; // 标记 SSE 是否已收到终态事件

        // HTTP/2 SSE
        const token = localStorage.getItem('auth_token');
        const streamUrl = getUrlWithToken(`/api/v1/translate/${taskId}/stream`, token);
        const eventSource = new EventSource(streamUrl);

        eventSource.addEventListener('progress', (e) => {
            try {
                const data = JSON.parse(e.data);
                setProgress((prev) => ({
                    ...prev,
                    status: data.status,
                    stage: data.stage,
                }));
            } catch (err) {
                console.error('Failed to parse progress event:', err);
            }
        });

        eventSource.addEventListener('completed', (e) => {
            try {
                sseTerminated = true;
                const data = JSON.parse(e.data);
                setProgress({
                    status: 'completed',
                    stage: data.stage || '已完成',
                    downloadUrl: data.download_url ? getUrlWithToken(`${data.download_url}`, token) : undefined,
                });
                eventSource.close();
            } catch (err) {
                console.error('Failed to parse completed event:', err);
            }
        });

        eventSource.addEventListener('failed', (e) => {
            try {
                sseTerminated = true;
                const data = JSON.parse(e.data);
                setProgress({
                    status: 'failed',
                    stage: data.stage || '失败',
                    error: data.error,
                });
                eventSource.close();
            } catch (err) {
                console.error('Failed to parse failed event:', err);
            }
        });

        /**
         * 回退轮询：SSE 连接断开且未收到终态事件时，
         * 通过 REST API 轮询获取任务真实状态。
         */
        const startFallbackPolling = (currentTaskId: string) => {
            if (pollingRef.current) return; // 已在轮询中
            console.log('[useTranslationProgress] SSE 断开，启动回退轮询');

            setProgress((prev) => ({
                ...prev,
                stage: '连接中断，正在获取状态...',
            }));

            pollingRef.current = setInterval(async () => {
                try {
                    const taskStatus = await translationApi.getTaskStatus(currentTaskId);

                    if (taskStatus.status === 'completed') {
                        const downloadUrl = taskStatus.download_url
                            ? getUrlWithToken(taskStatus.download_url, token)
                            : undefined;
                        setProgress({
                            status: 'completed',
                            stage: '已完成',
                            downloadUrl,
                        });
                        if (pollingRef.current) {
                            clearInterval(pollingRef.current);
                            pollingRef.current = null;
                        }
                    } else if (taskStatus.status === 'failed') {
                        setProgress({
                            status: 'failed',
                            stage: '转换失败',
                            error: taskStatus.error_message || '翻译失败，请重试',
                        });
                        if (pollingRef.current) {
                            clearInterval(pollingRef.current);
                            pollingRef.current = null;
                        }
                    } else {
                        // 仍在处理中
                        setProgress((prev) => ({
                            ...prev,
                            status: taskStatus.status as ProgressState['status'],
                            stage: '处理中...',
                        }));
                    }
                } catch (err) {
                    console.error('[useTranslationProgress] 回退轮询失败:', err);
                }
            }, 3000); // 每 3 秒轮询一次
        };

        eventSource.onerror = () => {
            // 如果已经收到终态事件，忽略后续的 onerror
            if (sseTerminated) return;

            if (eventSource.readyState === EventSource.CLOSED) {
                // 连接已彻底关闭，浏览器不会再重连
                // 启动回退轮询获取真实状态
                eventSource.close();
                startFallbackPolling(taskId);
            } else {
                // readyState === CONNECTING，浏览器正在自动重连
                setProgress((prev) => {
                    if (prev.status !== 'completed' && prev.status !== 'failed') {
                        return { ...prev, stage: '连接中断，正在重连...' };
                    }
                    return prev;
                });
            }
        };

        return () => {
            eventSource.close();
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [taskId]);

    return progress;
}
